import type { GetContractTrendResponse } from '@ebalance/proto/bffuser/v1/customer_service_pb'
import { ContractTrendEventKind } from '@ebalance/proto/bffuser/v1/customer_service_pb'

import type { CivilDate } from '~/models/civilDate'
import { decimalToNumberOr } from '~/models/decimal'
import type { VoltageClass } from '~/models/voltageBreakdown'
import { toVoltageClass } from '~/pages/CustomerInfo/models/converters'

/**
 * 契約推移タブの表示用の形 (AC-CIN-002〜005)。 日ごとの集計 (区分「契約」だけ・供給期間内・その日に効いている
 * 契約kW・電圧階級別の合計・件数・KPI) は backend の GetContractTrend が計算し、 ここでは受け取った値を
 * 年度 / 月の括り・グラフの行・目盛りに並べ替えるだけ (集計を画面で作り直さない)。
 *
 * - グラフは日ごとの値 (月の途中の増減を潰さない)。
 * - 電圧階級の選択は表示する列・変動イベントを絞る (合計は常に全電圧階級の和)。
 */

export const VOLTAGES: VoltageClass[] = ['low', 'high', 'extraHigh']

export type TrendDay = {
  date: CivilDate
  kw: Record<VoltageClass, number>
  totalKw: number
  count: number
}

export type TrendEvent = {
  date: CivilDate
  kind: 'start' | 'end'
  customerName: string
  voltage: VoltageClass
  kw: number
}

export type TrendMonth = {
  year: number
  month: number
  /** `YYYY-MM` */
  key: string
  days: TrendDay[]
}

export type TrendFiscalYear = {
  /** 年度 (4 月始まり)。 */
  fy: number
  months: TrendMonth[]
}

export type TrendChartRow = {
  /** `YYYY/MM/DD` (日ごとに 1 行) */
  label: string
  kw: Record<VoltageClass, number>
  totalKw: number
  count: number
}

export type TrendKpi = {
  latest: TrendDay
  avgTotalKw: number
  minTotalKw: number
  maxTotalKw: number
}

export type ContractTrend = {
  days: TrendDay[]
  months: TrendMonth[]
  events: TrendEvent[]
  chartRows: TrendChartRow[]
  /** 日が 1 日も無い時は undefined。 */
  kpi: TrendKpi | undefined
}

/** 取得前・失敗時に出す空の集計。 */
export const EMPTY_CONTRACT_TREND: ContractTrend = {
  days: [],
  months: [],
  events: [],
  chartRows: [],
  kpi: undefined,
}

/** 年度 (4 月始まり)。 */
export const fiscalYearOf = (date: CivilDate): number =>
  date.month >= 4 ? date.year : date.year - 1

/** 月の並びを年度ごとに括る (範囲が 1 年度だけでも 1 件返す)。 */
export const groupByFiscalYear = (months: TrendMonth[]): TrendFiscalYear[] => {
  const groups: TrendFiscalYear[] = []
  for (const m of months) {
    const fy = fiscalYearOf({ year: m.year, month: m.month, day: 1 })
    const last = groups[groups.length - 1]
    if (last !== undefined && last.fy === fy) last.months.push(m)
    else groups.push({ fy, months: [m] })
  }
  return groups
}

/** 年度範囲 → 日付範囲 (開始年度の 4/1 〜 終了年度の翌 3/31)。 */
export const fiscalYearRange = (
  fyFrom: number,
  fyTo: number,
): { from: CivilDate; to: CivilDate } => ({
  from: { year: fyFrom, month: 4, day: 1 },
  to: { year: fyTo + 1, month: 3, day: 31 },
})

/**
 * 横軸の目盛り: 月の初日の行の label。 月が多い範囲 (複数年度) は間引いて maxTicks 個以内にする
 * (間引いた目盛りも月の初日のまま)。
 */
export const monthTicks = (
  rows: readonly TrendChartRow[],
  maxTicks = 14,
): string[] => {
  const firsts = rows
    .filter(
      (r, i) =>
        i === 0 || r.label.slice(0, 7) !== rows[i - 1].label.slice(0, 7),
    )
    .map((r) => r.label)
  const step = Math.max(1, Math.ceil(firsts.length / maxTicks))
  return firsts.filter((_, i) => i % step === 0)
}

const toCivil = (
  d: { year: number; month: number; day: number } | undefined,
): CivilDate => ({
  year: d?.year ?? 0,
  month: d?.month ?? 0,
  day: d?.day ?? 0,
})

const pad = (n: number): string => String(n).padStart(2, '0')

/**
 * GetContractTrend の応答を表示用に並べる。 voltages は変動イベントに出す電圧階級
 * (日ごとの値と KPI は常に全電圧階級)。
 */
export const toContractTrend = (
  response: GetContractTrendResponse,
  voltages: ReadonlySet<VoltageClass>,
): ContractTrend => {
  const days: TrendDay[] = response.days.map((d) => {
    const kw = {
      low: decimalToNumberOr(d.lowKw),
      high: decimalToNumberOr(d.highKw),
      extraHigh: decimalToNumberOr(d.extraHighKw),
    }
    return {
      date: toCivil(d.date),
      kw,
      totalKw: kw.low + kw.high + kw.extraHigh,
      count: d.count,
    }
  })

  const events: TrendEvent[] = response.events
    .map((e) => ({
      date: toCivil(e.date),
      kind:
        e.kind === ContractTrendEventKind.END
          ? ('end' as const)
          : ('start' as const),
      customerName: e.customerName,
      voltage: toVoltageClass(e.voltageClass),
      kw: decimalToNumberOr(e.contractKw),
    }))
    .filter((e) => voltages.has(e.voltage))

  const months: TrendMonth[] = []
  for (const day of days) {
    const key = `${day.date.year}-${pad(day.date.month)}`
    const last = months[months.length - 1]
    if (last?.key === key) last.days.push(day)
    else
      months.push({
        year: day.date.year,
        month: day.date.month,
        key,
        days: [day],
      })
  }

  const chartRows: TrendChartRow[] = days.map((d) => ({
    label: `${d.date.year}/${pad(d.date.month)}/${pad(d.date.day)}`,
    kw: { ...d.kw },
    totalKw: d.totalKw,
    count: d.count,
  }))

  const kpi: TrendKpi | undefined =
    response.kpi === undefined || days.length === 0
      ? undefined
      : {
          latest: days[days.length - 1],
          avgTotalKw: decimalToNumberOr(response.kpi.avgTotalKw),
          minTotalKw: decimalToNumberOr(response.kpi.minTotalKw),
          maxTotalKw: decimalToNumberOr(response.kpi.maxTotalKw),
        }

  return { days, months, events, chartRows, kpi }
}
